/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import {
  downloadFile,
  formatFecha,
  Boton,
  Titulo,
  CampoInformacion,
  ArchivoPreview,
  TablaFiltrable
} from "../../shared";

function BotonDescargarArchivo({ archivoId, nombreArchivo, toast }) {
  const [loader, setLoader] = useState(false);

  function descargar() {
    setLoader(true);
    downloadFile(
      `admin/archivo/${archivoId}`,
      nombreArchivo,
      () => {
        toast.current?.show({
          severity: "success",
          summary: "Descargado",
          detail: "Archivo descargado"
        });
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar archivo",
          sticky: true
        });
        setLoader(false);
      }
    );
  }

  return (
    <Boton icono negro callback={descargar} loader={loader}>
      <i className="pi pi-download" />
    </Boton>
  );
}

export default function TarjetaDetallesSolicitud({ solicitud }) {
  const [loaderDescargarArchivo, setLoaderDescargarArchivo] = useState(false);
  const [loaderDescargarTodos, setLoaderDescargarTodos] = useState(false);

  const toast = useRef(null);
  const navigate = useNavigate();

  // Descarga el archivo de la solicitud
  function descargarArchivo() {
    setLoaderDescargarArchivo(true);
    downloadFile(
      `admin/archivo/${solicitud.archivoproductos.id}`,
      solicitud.archivoproductos.nombredescarga,
      () => {
        toast.current?.show({
          severity: "success",
          summary: "Descargado",
          detail: "Archivo descargado"
        });
        setLoaderDescargarArchivo(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar archivo",
          sticky: true
        });
        setLoaderDescargarArchivo(false);
      }
    );
  }

  // Descarga todos los archivos adicionales en un ZIP
  function descargarTodosLosArchivos() {
    setLoaderDescargarTodos(true);
    downloadFile(
      `admin/archivo/solicitud/${solicitud.id}/zip`,
      `archivos_adicionales_solicitud_${solicitud.id}.zip`,
      () => {
        toast.current?.show({
          severity: "success",
          summary: "Descargado",
          detail: "Todos los archivos descargados"
        });
        setLoaderDescargarTodos(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar archivos",
          sticky: true
        });
        setLoaderDescargarTodos(false);
      }
    );
  }

  const clienteDisplay =
    solicitud.cliente.nombrefantasia &&
    solicitud.cliente.nombrefantasia !== solicitud.cliente.razonsocial
      ? `${solicitud.cliente.razonsocial} (${solicitud.cliente.nombrefantasia})`
      : solicitud.cliente.razonsocial;

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Card>
        <Titulo>Detalles de la solicitud</Titulo>

        <div className="grid">
          <CampoInformacion nombreCampo="Cliente">
            <span
              css={{ cursor: "pointer", "&:hover": { color: "var(--principal)" } }}
              onClick={(e) => {
                const url = `/clientes/${solicitud.cliente.id}/informacion`;
                e.metaKey || e.ctrlKey ? window.open(url, "_blank") : navigate(url);
              }}
            >
              {clienteDisplay}
            </span>
          </CampoInformacion>

          <CampoInformacion nombreCampo="Nombre Fantasía">
            {solicitud.nombrefantasia || "-"}
          </CampoInformacion>

          <CampoInformacion nombreCampo="Fecha">
            {formatFecha(solicitud.fecha)}
          </CampoInformacion>

          <Divider />

          <CampoInformacion nombreCampo="Tipo de certificado">
            {solicitud.certificadotipo.nombre}
          </CampoInformacion>

          <Divider />

          <CampoInformacion nombreCampo="Nombre fábrica">
            {solicitud.sitioFabrica?.nombre || "-"}
          </CampoInformacion>

          <CampoInformacion nombreCampo="Dirección fábrica">
            {solicitud.sitioFabrica?.direccion || "-"}
          </CampoInformacion>

          <CampoInformacion nombreCampo="Nombre fabricante">
            {solicitud.sitioFabricante?.nombre || "-"}
          </CampoInformacion>

          <CampoInformacion nombreCampo="Dirección fabricante">
            {solicitud.sitioFabricante?.direccion || "-"}
          </CampoInformacion>

          <Divider />

          <CampoInformacion nombreCampo="Archivo de productos" anchoCompleto>
            {solicitud.archivoproductos ? (
              <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <ArchivoPreview
                  nombre={solicitud.archivoproductos.nombredescarga}
                  hash={solicitud.archivoproductos.hash}
                />
                <Boton
                  icono
                  negro
                  chico
                  callback={descargarArchivo}
                  loader={loaderDescargarArchivo}
                >
                  <i className="pi pi-download" />
                </Boton>
              </div>
            ) : (
              "-"
            )}
          </CampoInformacion>
        </div>

        {solicitud.archivosadicionales &&
          solicitud.archivosadicionales.length > 0 && (
            <>
              <Divider />
              <Titulo
                botones={
                  <Boton
                    icono
                    negro
                    callback={descargarTodosLosArchivos}
                    loader={loaderDescargarTodos}
                    title="Descargar todos"
                  >
                    <i className="pi pi-download" />
                  </Boton>
                }
              >
                Archivos adicionales
              </Titulo>

              <TablaFiltrable conFiltros={false} value={solicitud.archivosadicionales} size="small" nombreArchivo="archivos_adicionales_solicitud">
                <Column
                  field="nombredescarga"
                  header="Archivo"
                  body={(rowData) => (
                    <ArchivoPreview
                      nombre={rowData.nombredescarga}
                      hash={rowData.hash}
                    />
                  )}
                />
                <Column
                  body={(rowData) => (
                    <BotonDescargarArchivo
                      archivoId={rowData.id}
                      nombreArchivo={rowData.nombredescarga}
                      toast={toast}
                    />
                  )}
                  style={{ width: "4rem" }}
                  bodyStyle={{ textAlign: "right" }}
                />
              </TablaFiltrable>
            </>
          )}
      </Card>
    </>
  );
}
